import {
  Chat,
  MaybeOptimisticChatTextMessage,
} from "@lazarentertainment/public-chat-web"
import { isNotOptimisticMessage } from "@lazarentertainment/public-chat-web/dist/src/messagesState"
import { ControlledMenu, useMenuState } from "@szhsin/react-menu"
import ReactionPickerIcon from "assets/icons/reactionPicker.svg"
import { useRef } from "react"
import styled from "styled-components"

import { ChatView } from "../../constants"

import { ReactionPicker } from "./ChatReactionPicker"

const Menu = styled(ControlledMenu)`
  z-index: 1;

  .szh-menu {
    background: none;
    box-shadow: none;
    overflow: visible;
  }
`

const MenuButton = styled.button`
  align-items: center;
  align-self: center;
  background: unset;
  border: unset;
  display: flex;
  justify-content: center;

  &:hover .message-menu-icon {
    opacity: 1;
  }
`

const MenuIcon = styled(ReactionPickerIcon)<{ "data-uiview": ChatView }>`
  height: 20px;
  fill: ${(props) => props.theme.colors.darkMed};
  opacity: 0.7;

  &[data-uiview="${ChatView.OVERLAY}"] {
    fill: ${(props) => props.theme.colors.white};
  }
`

const Container = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: row;

  ${({ $open }) => $open && `visibility: visible !important;`}
`

export const ReactionMenuButton = ({
  message,
  submitMessageReaction,
  uiView,
}: {
  message: MaybeOptimisticChatTextMessage
  submitMessageReaction: Chat["toggleReaction"]
  uiView: ChatView
}) => {
  const menuButtonAnchorRef = useRef(null)
  const [{ state: menuState }, toggleReactionPickerMenu] = useMenuState()
  const skipOpen = useRef(false)

  return (
    <Container
      className="chat-message-menu-option"
      $open={menuState === "open"}
    >
      <Menu
        anchorRef={menuButtonAnchorRef}
        state={menuState}
        onClose={({ reason }) => {
          if (reason === "blur") {
            // NOTE: Due to the issue outlined in https://github.com/szhsin/react-menu/issues/56, this
            // workaround has been added in order to fix a bug where clicking the menu button while
            // the menu is already open prevents the menu from closing properly.
            skipOpen.current = true
            setTimeout(() => {
              skipOpen.current = false
            }, 300)
          }
          toggleReactionPickerMenu(false)
        }}
        position="auto"
        align="center"
        direction="top"
      >
        {
          // Reactions are currently only supported for not-optimistic messages
          isNotOptimisticMessage(message) ? (
            <ReactionPicker
              message={message}
              submitMessageReaction={submitMessageReaction}
              closeMenu={() => toggleReactionPickerMenu(false)}
            />
          ) : null
        }
      </Menu>
      <MenuButton
        ref={menuButtonAnchorRef}
        onClick={() => !skipOpen.current && toggleReactionPickerMenu(true)}
        data-robot="chat-message-reaction-button"
      >
        <MenuIcon className="message-menu-icon" data-uiview={uiView} />
      </MenuButton>
    </Container>
  )
}
